// Chest system. Chests spawn periodically; walking onto one opens it,
// spawning 3–6 items tagged per faction. Walking over an item picks it
// up. Right-team tag = benefit, wrong-team tag = poison.
//
// This file is transport-neutral so both the real server (party/town.ts)
// and the SoloStub can drive the same sim without divergence.

import type { MythicalId, Role } from '../net/protocol';
import { rollMythicForLoot } from './mythicals';
import type { AABB2 } from './arenas';

export const CHEST_TUNING = {
  SPAWN_INTERVAL_SEC: 25,
  FIRST_SPAWN_SEC: 15,
  MAX_ACTIVE: 3,
  OPEN_RADIUS: 1.6,
  // CP3: ITEM_PICKUP_RADIUS bumped 1.0 → 1.4 because the magnet
  // hand-feeds items to the player; collision should feel forgiving
  // once the magnet has them in motion.
  ITEM_PICKUP_RADIUS: 1.4,
  ITEM_LIFETIME_SEC: 30,
  SPAWN_BOUNDS: { xMin: -32, xMax: 32, zMin: -32, zMax: 32 },

  // CP3 magnet — same-team players within MAGNET_RADIUS pull loot
  // toward them. Speed scales up as the item gets closer for that
  // chunky "vacuumed in" Brotato/Vampire Survivors feel.
  MAGNET_RADIUS: 8.0,
  MAGNET_SPEED: 14.0,

  XP_VALUE: 30,
  HP_VALUE: 30,
  POISON_DAMAGE: 15,
} as const;

export type ChestItemKind = 'xp' | 'hp' | 'mythic';

export type ChestSnap = {
  id: number;
  x: number;
  z: number;
};

export type LootSnap = {
  id: number;
  x: number;
  z: number;
  role: Role;
  kind: ChestItemKind;
  mythic?: MythicalId;
};

type ServerChest = { id: number; x: number; z: number };
type ServerLoot = {
  id: number;
  x: number;
  z: number;
  role: Role;
  kind: ChestItemKind;
  mythic?: MythicalId;
  expiresAtMs: number;
};

export type AttackerPos = { id: string; role: Role; x: number; z: number };

export type ChestOpenEvent = { chestId: number; openerId: string; x: number; z: number };
export type ChestPickupEvent = {
  itemId: number;
  pickerId: string;
  kind: ChestItemKind;
  mythic?: MythicalId;
  role: Role;
  correct: boolean;
  x: number;
  z: number;
};

export class ChestManager {
  private chests: ServerChest[] = [];
  private loot: ServerLoot[] = [];
  private nextId = 1;
  private nextSpawnInSec: number = CHEST_TUNING.FIRST_SPAWN_SEC;
  // CP6 — per-mode overrides. Defaults match CHEST_TUNING constants
  // until the server passes a per-arena value at warmup→playing.
  private intervalSec: number = CHEST_TUNING.SPAWN_INTERVAL_SEC;
  private maxActive: number = CHEST_TUNING.MAX_ACTIVE;
  // Per-arena spawn box. Defaults to the legacy Classic-80u box until
  // the server calls setSpawnBounds(arena.chestBounds) at warmup.
  private spawnBounds: AABB2 = CHEST_TUNING.SPAWN_BOUNDS;
  // CP5 fix — when set, ALL spawned items get tagged with this faction
  // instead of the random 50/50. Used by PvE so the single present
  // faction can pick up everything (otherwise half the loot is dead
  // weight that can't be magnetized or collected).
  private forceItemRole: Role | null = null;

  setTuning(intervalSec: number, maxActive: number): void {
    this.intervalSec = Math.max(2, intervalSec);
    this.maxActive = Math.max(0, maxActive);
  }

  // Set per-arena chest spawn bounds. Call at warmup→playing.
  setSpawnBounds(b: AABB2): void {
    this.spawnBounds = b;
  }

  setForceItemRole(role: Role | null): void {
    this.forceItemRole = role;
  }

  reset() {
    this.chests = [];
    this.loot = [];
    this.nextId = 1;
    this.nextSpawnInSec = CHEST_TUNING.FIRST_SPAWN_SEC;
  }

  tick(
    dt: number,
    nowMs: number,
    attackers: ReadonlyArray<AttackerPos>,
  ): { opened: ChestOpenEvent[]; pickups: ChestPickupEvent[] } {
    const opened: ChestOpenEvent[] = [];
    const pickups: ChestPickupEvent[] = [];

    // Spawn pacing — uses per-mode overrides set by the server at
    // warmup→playing (see ArenaConfig.chestIntervalSec / chestMaxActive).
    this.nextSpawnInSec -= dt;
    if (this.nextSpawnInSec <= 0 && this.chests.length < this.maxActive) {
      this.spawnChest();
      this.nextSpawnInSec = this.intervalSec;
    }

    // Chest open detection
    const openRadiusSq = CHEST_TUNING.OPEN_RADIUS * CHEST_TUNING.OPEN_RADIUS;
    const survivingChests: ServerChest[] = [];
    for (const c of this.chests) {
      let opener: AttackerPos | null = null;
      for (const a of attackers) {
        const dx = a.x - c.x;
        const dz = a.z - c.z;
        if (dx * dx + dz * dz <= openRadiusSq) { opener = a; break; }
      }
      if (opener) {
        const count = 3 + Math.floor(Math.random() * 4); // 3..6
        for (let i = 0; i < count; i++) {
          const ang = (i / count) * Math.PI * 2 + Math.random() * 0.4;
          const r = 1.2 + Math.random() * 0.7;
          const role: Role = this.forceItemRole ?? (Math.random() < 0.5 ? 'ext' : 'int');
          // ~8% of items are purple mythic orbs instead of xp/hp. Rolled
          // per-item so a single chest can yield one mythic among 3-6
          // regular drops.
          const mythic = rollMythicForLoot();
          const kind: ChestItemKind = mythic ? 'mythic' : (Math.random() < 0.5 ? 'xp' : 'hp');
          this.loot.push({
            id: this.nextId++,
            x: c.x + Math.cos(ang) * r,
            z: c.z + Math.sin(ang) * r,
            role,
            kind,
            mythic: mythic ?? undefined,
            expiresAtMs: nowMs + CHEST_TUNING.ITEM_LIFETIME_SEC * 1000,
          });
        }
        opened.push({ chestId: c.id, openerId: opener.id, x: c.x, z: c.z });
      } else {
        survivingChests.push(c);
      }
    }
    this.chests = survivingChests;

    // CP3 magnet pull — items lerp toward the nearest same-team player
    // within MAGNET_RADIUS. Wrong-team poison items DON'T magnetize so
    // enemies can't drag a green orb away from us.
    const magnetSq = CHEST_TUNING.MAGNET_RADIUS * CHEST_TUNING.MAGNET_RADIUS;
    for (const item of this.loot) {
      if (nowMs >= item.expiresAtMs) continue;
      let nearest: AttackerPos | null = null;
      let nearestDsq = magnetSq;
      for (const a of attackers) {
        // XP items are universal — magnetize to ANY nearby player.
        if (item.kind !== 'xp' && a.role !== item.role) continue;
        const dx = a.x - item.x;
        const dz = a.z - item.z;
        const dsq = dx * dx + dz * dz;
        if (dsq < nearestDsq) {
          nearestDsq = dsq;
          nearest = a;
        }
      }
      if (nearest) {
        const d = Math.sqrt(nearestDsq) || 0.001;
        const ux = (nearest.x - item.x) / d;
        const uz = (nearest.z - item.z) / d;
        // Speed ramps 1× at the edge → 1.6× near the player so the
        // last meter feels snappy (vs a constant glide).
        const t = 1 - (d / CHEST_TUNING.MAGNET_RADIUS);
        const speed = CHEST_TUNING.MAGNET_SPEED * (1 + t * 0.6);
        item.x += ux * speed * dt;
        item.z += uz * speed * dt;
      }
    }

    // Item pickup detection
    const pickRadiusSq = CHEST_TUNING.ITEM_PICKUP_RADIUS * CHEST_TUNING.ITEM_PICKUP_RADIUS;
    const survivingLoot: ServerLoot[] = [];
    for (const item of this.loot) {
      if (nowMs >= item.expiresAtMs) continue;
      let picker: AttackerPos | null = null;
      for (const a of attackers) {
        const dx = a.x - item.x;
        const dz = a.z - item.z;
        if (dx * dx + dz * dz <= pickRadiusSq) { picker = a; break; }
      }
      if (picker) {
        // All loot is universal — first player to touch it gets the
        // benefit, regardless of the visual role-tag on the orb. XP
        // always grants XP, HP always heals, mythic always equips —
        // no more poison-on-wrong-role. First come, first served.
        const correct = true;
        pickups.push({
          itemId: item.id,
          pickerId: picker.id,
          kind: item.kind,
          mythic: item.mythic,
          role: item.role,
          correct,
          x: item.x,
          z: item.z,
        });
      } else {
        survivingLoot.push(item);
      }
    }
    this.loot = survivingLoot;

    return { opened, pickups };
  }

  private spawnChest() {
    const b = this.spawnBounds;
    const x = b.xMin + Math.random() * (b.xMax - b.xMin);
    const z = b.zMin + Math.random() * (b.zMax - b.zMin);
    this.chests.push({ id: this.nextId++, x, z });
  }

  // Debug-only: drop a chest at the given XZ ignoring spawn cap and bounds.
  forceSpawn(x: number, z: number): number {
    const id = this.nextId++;
    this.chests.push({ id, x, z });
    return id;
  }

  snapshot(): { chests: ChestSnap[]; loot: LootSnap[] } {
    return {
      chests: this.chests.map((c) => ({ id: c.id, x: c.x, z: c.z })),
      loot: this.loot.map((l) => ({
        id: l.id, x: l.x, z: l.z, role: l.role, kind: l.kind,
        mythic: l.mythic,
      })),
    };
  }
}
